import { useEffect, useState } from 'react';
import { Check, Loader2 } from 'lucide-react';
import { buscarConfigCompleta, salvarConfig, type AppConfig } from '@/features/config/api';

const VAZIO: AppConfig = {
  nome_loja: '',
  partida_endereco: '',
  partida_lat: '',
  partida_lng: '',
  pedido_minimo: '',
  horario_entrega: '',
  horario_corte: '',
  dias_funcionamento: '',
  numero_operador: '',
  tema_accent: '',
};

export default function ConfiguracoesPage() {
  const [form, setForm] = useState<AppConfig>(VAZIO);
  const [carregando, setCarregando] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [feedback, setFeedback] = useState<string | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const cfg = await buscarConfigCompleta();
        if (alive) setForm(cfg);
      } catch (e) {
        if (alive) setErro((e as Error).message);
      } finally {
        if (alive) setCarregando(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  function set<K extends keyof AppConfig>(k: K, v: AppConfig[K]) {
    setForm((f) => ({ ...f, [k]: v }));
    setFeedback(null);
  }

  async function salvar() {
    setSalvando(true);
    setErro(null);
    setFeedback(null);
    try {
      const { config, geocodificado } = await salvarConfig(form);
      setForm(config);
      setFeedback(geocodificado ? 'Salvo. Endereço de partida geocodificado.' : 'Configurações salvas.');
    } catch (e) {
      setErro((e as Error).message);
    } finally {
      setSalvando(false);
    }
  }

  return (
    <div className="flex flex-col gap-s7 max-w-[820px]">
      <header>
        <h1 className="t-overline mb-s3">Configurações</h1>
        <p className="font-display italic text-[40px] leading-tight text-fg-1 tracking-[-0.02em]">
          Como o jardim opera.
        </p>
      </header>

      {carregando ? (
        <p className="font-display italic text-[18px] text-fg-3">Carregando…</p>
      ) : (
        <>
          <Secao titulo="Loja">
            <Campo label="Nome da loja">
              <Input value={form.nome_loja} onChange={(v) => set('nome_loja', v)} placeholder="Floricultura Noturna" />
            </Campo>
            <Campo label="Número do operador (WhatsApp que recebe os pedidos)">
              <Input value={form.numero_operador} onChange={(v) => set('numero_operador', v)} placeholder="+5562999999999" />
            </Campo>
          </Secao>

          <Secao titulo="Entrega e rota">
            <Campo label="Ponto de partida (endereço da loja)" hint="Geocodifica automático ao salvar.">
              <Input
                value={form.partida_endereco}
                onChange={(v) => set('partida_endereco', v)}
                placeholder="Rua T-50, Setor Bueno, Goiânia-GO"
              />
            </Campo>
            <div className="grid grid-cols-2 gap-s4">
              <Campo label="Pedido mínimo (R$)">
                <Input value={form.pedido_minimo} onChange={(v) => set('pedido_minimo', v)} placeholder="200" />
              </Campo>
              <Campo label="Dias de funcionamento">
                <Input value={form.dias_funcionamento} onChange={(v) => set('dias_funcionamento', v)} placeholder="Seg a Sex" />
              </Campo>
              <Campo label="Horário de entrega (a partir de)">
                <Input value={form.horario_entrega} onChange={(v) => set('horario_entrega', v)} placeholder="19:00" />
              </Campo>
              <Campo label="Horário de corte da rota">
                <Input value={form.horario_corte} onChange={(v) => set('horario_corte', v)} placeholder="19:00" />
              </Campo>
            </div>
          </Secao>

          <div className="flex items-center gap-s4">
            <button
              onClick={salvar}
              disabled={salvando}
              className="btn-accent inline-flex items-center gap-s3 h-[40px] px-s6 rounded-sm border text-fg-1 text-[13px] font-semibold transition-all duration-fast active:scale-[0.98] disabled:opacity-60"
            >
              {salvando ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} strokeWidth={1.8} />}
              Salvar configurações
            </button>
            {feedback && <span className="font-body text-[12.5px] text-moss-300">{feedback}</span>}
            {erro && <span className="font-body text-[12.5px] text-petal-300">{erro}</span>}
          </div>
        </>
      )}
    </div>
  );
}

function Secao({ titulo, children }: { titulo: string; children: React.ReactNode }) {
  return (
    <section className="bg-panel border border-[var(--border)] rounded-md p-s7 flex flex-col gap-s5">
      <span className="t-overline">{titulo}</span>
      {children}
    </section>
  );
}

function Campo({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-s2">
      <span className="font-body text-[12px] text-fg-2">{label}</span>
      {children}
      {hint && <span className="font-body text-[11px] text-fg-3">{hint}</span>}
    </label>
  );
}

function Input({
  value,
  onChange,
  placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}) {
  return (
    <input
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full bg-input border border-[var(--border)] rounded-sm px-s4 h-[40px] font-body text-[13px] text-fg-1 outline-none focus:border-moss-400"
    />
  );
}
